import React, { useEffect } from 'react';
import { Drawer, Form, Select, DatePicker, TimePicker, Input, Switch, Segmented, Button, Space, Row, Col, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { scheduleApi, ScheduleException, ExceptionWritePayload } from '../../services/scheduleApi';

/** Minimal tech shape the drawer needs — matches the {id, first_name,
 *  last_name} rows TechnicianSelect/useSchedulingData already load, so
 *  callers can pass either straight through without remapping. */
export interface BlockTimeTech {
  id: number;
  first_name?: string;
  last_name?: string;
  name?: string;
}

/** Category shown in the picker → the backend's exception_type enum. Two
 *  categories (Lunch/Break, Appt/Errand) both map to 'break' — the
 *  distinction is display-only for staff, the server only knows 'break'. */
type BlockCategory = 'lunch_break' | 'time_off' | 'appt_errand' | 'holiday';

const CATEGORY_TO_EXCEPTION_TYPE: Record<BlockCategory, ExceptionWritePayload['exception_type']> = {
  lunch_break: 'break',
  time_off: 'time_off',
  appt_errand: 'break',
  holiday: 'holiday',
};

const CATEGORY_OPTIONS: { value: BlockCategory; label: string }[] = [
  { value: 'lunch_break', label: 'Lunch / Break' },
  { value: 'time_off', label: 'Time off' },
  { value: 'appt_errand', label: 'Appointment / Errand' },
  { value: 'holiday', label: 'Holiday (shop-wide)' },
];

export interface BlockTimeDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Fires after a successful create/update so the caller can reload + close. */
  onSaved: () => void;
  techs: BlockTimeTech[];
  defaultTechId?: number;
  /** Space-separated local 'YYYY-MM-DD HH:mm:ss', matching the API's format. */
  defaultStart?: string;
  defaultEnd?: string;
  /** When set, the drawer edits this existing exception instead of creating one. */
  editing?: ScheduleException | null;
  /** Whether the signed-in user may create shop-wide (holiday) blocks — gates
   *  both the Scope control's "Whole shop" option and scope='shop' payloads. */
  canShopWide: boolean;
}

interface FormValues {
  scope: 'tech' | 'shop';
  category: BlockCategory;
  user_id?: number;
  startDate: Dayjs;
  endDate: Dayjs;
  startTime: Dayjs;
  endTime: Dayjs;
  reason?: string;
  is_recurring: boolean;
}

const LOCAL_FMT = 'YYYY-MM-DD HH:mm:ss';

const techName = (t: BlockTimeTech): string =>
  t.name || `${t.first_name || ''} ${t.last_name || ''}`.trim() || `Tech #${t.id}`;

/** exception_type on a persisted row → the closest category option, for
 *  pre-filling the picker when editing (the reverse of CATEGORY_TO_EXCEPTION_TYPE
 *  is lossy for 'break', so it defaults back to Lunch/Break). */
const exceptionTypeToCategory = (t: ScheduleException['exception_type']): BlockCategory => {
  if (t === 'holiday') return 'holiday';
  if (t === 'time_off') return 'time_off';
  return 'lunch_break';
};

export const BlockTimeDrawer: React.FC<BlockTimeDrawerProps> = ({
  open,
  onClose,
  onSaved,
  techs,
  defaultTechId,
  defaultStart,
  defaultEnd,
  editing,
  canShopWide,
}) => {
  const [form] = Form.useForm<FormValues>();
  const scope = Form.useWatch('scope', form);
  const startDate = Form.useWatch('startDate', form);
  const endDate = Form.useWatch('endDate', form);
  // Multi-day = end date is a later calendar day than start date. Treated as
  // an all-day span (see handleSave) rather than a same-day time range, so
  // the start/end TimePickers and the weekly-recurrence toggle (which only
  // makes sense for a single day) are hidden.
  const isMultiDay = !!startDate && !!endDate && !endDate.isSame(startDate, 'day');
  const [saving, setSaving] = React.useState(false);

  // Seed the form whenever the drawer opens — either from the row being
  // edited, or from the defaults the calling view (calendar slot click, etc.)
  // passed in. Runs on every open (not just mount) so re-opening for a
  // different tech/slot doesn't show stale values from the previous open.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      const start = dayjs(editing.start_datetime);
      const end = dayjs(editing.end_datetime);
      // A row carrying a group_id is one slice of a shop-wide fan-out — edit it
      // as scope='shop' so the drawer labels it correctly and hides the inert
      // single-tech Select (the server updates the whole group by group_id).
      // Single-tech blocks (no group_id) keep scope='tech' unchanged.
      form.setFieldsValue({
        scope: editing.group_id ? 'shop' : 'tech',
        category: exceptionTypeToCategory(editing.exception_type),
        user_id: editing.user_id,
        startDate: start,
        endDate: end,
        startTime: start,
        endTime: end,
        reason: editing.reason,
        is_recurring: editing.is_recurring,
      });
      return;
    }
    const start = defaultStart ? dayjs(defaultStart) : dayjs();
    const end = defaultEnd ? dayjs(defaultEnd) : start.add(1, 'hour');
    form.setFieldsValue({
      scope: 'tech',
      category: 'lunch_break',
      user_id: defaultTechId,
      startDate: start,
      endDate: end,
      startTime: start,
      endTime: end,
      reason: undefined,
      is_recurring: false,
    });
  }, [open, editing, defaultStart, defaultEnd, defaultTechId, form]);

  const handleScopeChange = (value: string | number) => {
    form.setFieldValue('scope', value);
    if (value === 'shop') form.setFieldValue('category', 'holiday');
  };

  // Deliberately synchronous (no leading `await`): rc-field-form's
  // validateFields() is promise-based, and gating the API call behind it
  // would push the scheduleApi call past a microtask boundary the click
  // handler doesn't control. Trigger AntD's built-in validation UI
  // fire-and-forget (for the red-border/error affordance) but gate the
  // actual submit on synchronous manual checks so a click on Save either
  // calls the API in the same tick or not at all — deterministic, no
  // dangling half-submitted state.
  const handleSave = () => {
    form.validateFields().catch(() => {});
    const values = form.getFieldsValue(true) as FormValues;
    const isShop = canShopWide && values.scope === 'shop';

    if (!isShop && values.user_id == null) {
      message.error('Pick a technician');
      return;
    }
    if (!values.category) {
      message.error('Pick a category');
      return;
    }
    if (!values.startDate || !values.endDate) {
      message.error('Pick a start and end date');
      return;
    }
    const multiDay = !values.endDate.isSame(values.startDate, 'day');
    if (!multiDay && (!values.startTime || !values.endTime)) {
      message.error('Pick a date and time');
      return;
    }

    // Single day: compose start/end from the (shared) date + the two time
    // pickers, as before. Multi-day: treated as an all-day span across the
    // whole range — 00:00:00 on the start day through 23:59:00 on the end
    // day — so the (hidden) time pickers' stale values are never used.
    const start = multiDay
      ? values.startDate.hour(0).minute(0).second(0)
      : values.startDate.hour(values.startTime.hour()).minute(values.startTime.minute()).second(0);
    const end = multiDay
      ? values.endDate.hour(23).minute(59).second(0)
      : values.startDate.hour(values.endTime.hour()).minute(values.endTime.minute()).second(0);

    const exceptionType: ExceptionWritePayload['exception_type'] = isShop
      ? 'holiday'
      : CATEGORY_TO_EXCEPTION_TYPE[values.category];

    // Recurrence only applies to single-day blocks — the toggle is hidden for
    // multi-day, but force it off here too in case a stale `true` lingers in
    // form state from before the range was widened past one day.
    const isRecurring = !multiDay && !!values.is_recurring;

    const payload: ExceptionWritePayload = {
      scope: isShop ? 'shop' : 'tech',
      ...(isShop ? {} : { user_id: values.user_id }),
      exception_type: exceptionType,
      start_datetime: start.format(LOCAL_FMT),
      end_datetime: end.format(LOCAL_FMT),
      reason: values.reason,
      is_recurring: isRecurring,
      ...(isRecurring ? { recurrence_pattern: { frequency: 'weekly' as const, interval: 1 } } : {}),
    };

    setSaving(true);
    const request =
      editing?.id != null
        ? scheduleApi.updateException(editing.id, payload)
        : scheduleApi.createException(payload);
    request
      .then(() => onSaved())
      .catch(() => message.error('Could not save block'))
      .finally(() => setSaving(false));
  };

  return (
    <Drawer
      title={editing ? 'Edit block' : 'Block time'}
      open={open}
      onClose={onClose}
      destroyOnHidden
      width={420}
    >
      <Form form={form} layout="vertical">
        {canShopWide && (
          <Form.Item name="scope" label="Scope">
            <Segmented
              block
              options={[
                { label: 'This tech', value: 'tech' },
                { label: 'Whole shop', value: 'shop' },
              ]}
              onChange={handleScopeChange}
            />
          </Form.Item>
        )}

        {scope !== 'shop' && (
          <Form.Item name="user_id" label="Technician" rules={[{ required: true, message: 'Pick a technician' }]}>
            <Select
              placeholder="Select technician"
              options={techs.map((t) => ({ value: t.id, label: techName(t) }))}
            />
          </Form.Item>
        )}

        <Form.Item name="category" label="Category" rules={[{ required: true, message: 'Pick a category' }]}>
          <Select options={CATEGORY_OPTIONS} disabled={scope === 'shop'} />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="startDate" label="Start date" rules={[{ required: true, message: 'Pick a start date' }]}>
              <DatePicker
                style={{ width: '100%' }}
                onChange={(value) => {
                  // Keep "end defaults to start" as the start date moves —
                  // but only pull end forward when it would otherwise fall
                  // before the new start; a deliberately-widened range (end
                  // already past the new start) is left alone.
                  if (!value) return;
                  const currentEnd = form.getFieldValue('endDate') as Dayjs | undefined;
                  if (!currentEnd || currentEnd.isBefore(value, 'day')) {
                    form.setFieldValue('endDate', value);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="endDate" label="End date" rules={[{ required: true, message: 'Pick an end date' }]}>
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(current) => !!startDate && current.isBefore(startDate, 'day')}
              />
            </Form.Item>
          </Col>
        </Row>

        {!isMultiDay && (
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="startTime" label="Start time" rules={[{ required: true, message: 'Pick a start time' }]}>
                <TimePicker format="HH:mm" minuteStep={15} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="endTime" label="End time" rules={[{ required: true, message: 'Pick an end time' }]}>
                <TimePicker format="HH:mm" minuteStep={15} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Form.Item name="reason" label="Reason">
          <Input placeholder="Optional note" />
        </Form.Item>

        {!isMultiDay && (
          <Form.Item name="is_recurring" label="Repeat weekly" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}

        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save
          </Button>
        </Space>
      </Form>
    </Drawer>
  );
};
